import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { reactular } from '@vendhq/reactular'
import {
  ActionBar,
  Button,
  Column,
  Columns,
  Header,
  PageHeader,
  Row,
  Section,
  Switch,
  Text,
} from '@lightspeed/design-system-react'
import { T, TFunction, useT } from '@transifex/react'
import { NoPermissionsPage } from '@vend/react-utilities'
import { useDispatch } from 'react-redux'
import { getFeatureFromCache } from '../util/feature-cache'
import {
  FEATURE_ENABLE_REGISTER_ACCESS_CONTROL,
  PERMISSIONS,
} from '../constants'
import {
  usePosStateManager,
  useQuickKeysManager,
  useSaleSyncManager,
  useTrainingModeManager,
  useUserManager,
} from '../ng-compat/hooks'
import { UserManager } from '../managers/user-manager'
import { PosStateManager } from '../managers/pos-state-manager'
import { SaleSyncManager } from '../managers/sale-sync-manager'
import { setShowTrainingModeModal } from '../state/slices/uiStateSlice'
import { QuickKeysLayouts } from '../components/quick-keys/QuickKeysLayouts'
import { registerManager } from '../ng-compat/managers'
import { useIsOffline } from '../components/shared/useIsOffline'
import { OfflineModal } from '../components/shared/OfflineModal'
import { SaleModals } from '../components/sale/SaleModals'

const SettingsHeader: FunctionComponent = () => (
  <>
    <PageHeader>
      <T _str="Settings" _context="Heading for Webregister settings page" />
    </PageHeader>
    <ActionBar variant="page">
      <T _str="Manage your register settings" />
    </ActionBar>
  </>
)

const canAccessSettingsPage = (userManager: UserManager) => {
  if (!getFeatureFromCache(FEATURE_ENABLE_REGISTER_ACCESS_CONTROL)) {
    return true
  }

  return userManager.hasPermission(PERMISSIONS.SELL_SCREEN_SETTINGS_UPDATE)
}

const getTrainingButtonMessage = (
  posStateManager: PosStateManager,
  saleSyncManager: SaleSyncManager,
  t: TFunction
) => {
  if (posStateManager.getTrainingModeEnabled()) {
    return t('Training Mode is already enabled')
  }

  if (posStateManager.getActiveSaleLocalId()) {
    return t(
      'Sorry, you must complete the current open sale before entering Training Mode'
    )
  }

  if (saleSyncManager.failedToSyncSalesCount) {
    return t(
      'Sorry, you must resolve your errored sales before entering Training Mode'
    )
  }

  return null
}

export const Settings: FunctionComponent = () => {
  const t = useT()
  const dispatch = useDispatch()
  const userManager = useUserManager()
  const quickKeysManager = useQuickKeysManager()
  const posStateManager = usePosStateManager()
  const saleSyncManager = useSaleSyncManager()
  const trainingModeManager = useTrainingModeManager()
  const [hasAccess, setHasAccess] = useState(canAccessSettingsPage(userManager))
  const [qkEnabled, setQkEnabled] = useState(
    quickKeysManager.isQuickKeysEnabled()
  )
  const { isOffline } = useIsOffline()
  const [trainingMessage, setTrainingMessage] = useState(
    getTrainingButtonMessage(posStateManager, saleSyncManager, t)
  )
  const [canEditQuickKeys, setCanEditQuickKeys] = useState(
    quickKeysManager.hasPermission()
  )
  const [creatingLayout, setCreatingLayout] = useState(false)

  const activateTrainingMode = useCallback(() => {
    trainingModeManager.initiateTrainingMode()
    trainingModeManager.initiateTrainingTour()
    dispatch(setShowTrainingModeModal(true))
  }, [dispatch, trainingModeManager])

  const createNewLayout = useCallback(async () => {
    setCreatingLayout(true)

    const defaultLayoutName = t('New Layout')
    const newLayout = await quickKeysManager.createLayout(defaultLayoutName)

    quickKeysManager.navigateToEditState(newLayout, true)
    setCreatingLayout(false)
  }, [quickKeysManager, t])

  const setQuickKeysEnabled = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked
      setQkEnabled(checked)
      registerManager.enableQuickKeys(checked)
    },
    []
  )

  useEffect(() => {
    const updateAccess = () => {
      setHasAccess(canAccessSettingsPage(userManager))
      setCanEditQuickKeys(quickKeysManager.hasPermission())
    }

    const updateTrainingMessage = () => {
      setTrainingMessage(
        getTrainingButtonMessage(posStateManager, saleSyncManager, t)
      )
    }

    userManager.on('loggedInUserChange', updateAccess)
    posStateManager.on('trainingModeEnabledChange', updateTrainingMessage)
    posStateManager.on('activeSaleLocalIdChange', updateTrainingMessage)
    return () => {
      userManager.off('loggedInUserChange', updateAccess)
      posStateManager.off('trainingModeEnabledChange', updateTrainingMessage)
      posStateManager.off('activeSaleLocalIdChange', updateTrainingMessage)
    }
  }, [posStateManager, quickKeysManager, saleSyncManager, t, userManager])

  if (!hasAccess) {
    return (
      <>
        <SettingsHeader />
        <NoPermissionsPage
          copy={t(
            'You don’t have permission to access the register settings page. Switch to a user with permission.'
          )}
          promptToSwitchUser={true}
          backgroundColor="transparent"
        />
      </>
    )
  }

  return (
    <>
      <SaleModals />
      <SettingsHeader />
      <Section className="vd-mt5">
        <Header level="2" type="sub-heading" className="vd-header--settings">
          <T _str="Training Mode" />
        </Header>
        <Row>
          <Columns space={10} collapseBelow="tablet">
            <Column width="3/12">
              <div className="wr-settings-item-summary">
                <Text>
                  <T _str="Enable Training Mode if you’re new to Lightspeed Retail and want to learn the ropes. You’ll be selling like a pro in no time." />
                </Text>
              </div>
            </Column>
            <Column width="9/12">
              <Button
                onClick={activateTrainingMode}
                variant="go"
                disabled={Boolean(trainingMessage)}
              >
                <T _str="Enable Training Mode" />
              </Button>
              {trainingMessage ? (
                <Text tag="div" type="supplementary" className="vd-mt1">
                  {trainingMessage}
                </Text>
              ) : null}
            </Column>
          </Columns>
        </Row>
      </Section>
      <hr className="vd-hr" />
      {canEditQuickKeys ? (
        <Section>
          <Header level="2" type="sub-heading" className="vd-header--settings">
            <T _str="Quick Keys" />
          </Header>
          <Row>
            <Columns space={10} collapseBelow="tablet">
              <Column width="3/12">
                <T _str="Assign products as Quick Keys to help process sales faster. Rename, reposition and recolor keys, or organize your buttons into folders and pages." />
              </Column>
              <Column width="9/12">
                <div className="wr-media wr-media--center vd-mb10">
                  <Switch
                    onChange={setQuickKeysEnabled}
                    checked={qkEnabled}
                    className="wr-media wr-media-object"
                  ></Switch>
                  <div className="wr-media-body">
                    <Header
                      level="3"
                      className="vd-header--settings vd-mb1"
                      type={'sub-heading'}
                    >
                      <T _str="Enable Quick Keys for this register" />
                    </Header>
                    <Text type="supplementary">
                      <T _str="Toggle the switch to enable your Quick Keys for your register. You can turn this back on at anytime without losing your settings" />
                    </Text>
                  </div>
                </div>
                <Button
                  variant="go"
                  loading={creatingLayout}
                  onClick={createNewLayout}
                >
                  <T _str="Add Layout" />
                </Button>
                <QuickKeysLayouts />
              </Column>
            </Columns>
          </Row>
        </Section>
      ) : null}
      {isOffline ? <OfflineModal /> : null}
    </>
  )
}

export const SettingsComponent = reactular(Settings, [], 'reactWrapper')
